import {
    ILocationResponse,
    IssueStatusType,
} from '@kcftech/prometheustypescriptapiclient';
import { REFRESH_INTERVAL } from 'configs/constants';
import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import { useInfiniteMachineHealthIssuesDataSource } from 'custom_hooks/data_hooks/data_source/machineHealthIssuesDataSource';
import { subWeeks } from 'date-fns';
import { useOnIssueEvent } from 'pages/Desk/issues/issue_details/IssueEventsProvider';
import {
    buildFilterExpression,
    IssuePriorities,
    IssuesDefaultSort,
} from 'pages/Desk/issues/shared';
import { LocationIssuesHeader } from 'pages/Location/issues/LocationIssuesHeader';
import { LocationIssuesFilters } from 'pages/Location/issues/LocationIssuesQuickFilters';
import { LocationIssuesTable } from 'pages/Location/issues/LocationIssuesTable';
import { WidgetContainer } from 'pages/Location/issues/styled';
import { forwardRef, memo, useCallback, useImperativeHandle, useState } from 'react';
import {
    ACTIVE_ISSUE_STATUSES,
    PriorityFilter,
} from 'shared/issue_filters_context/types';
import { getId } from 'utils/data';

const defaultFilters: LocationIssuesFilters = {
    statusFilters: [],
    severityFilters: [],
    priorityFilters: [],
};

const TWO_WEEKS_AGO = subWeeks(new Date(), 2);

function getStatusType(statusFilter: string): IssueStatusType {
    switch (statusFilter) {
        case 'new':
            return IssueStatusType.New;
        case 'open':
            return IssueStatusType.Open;
        default:
            return IssueStatusType.New;
    }
}

function getPriorityValue(priorityFilter: PriorityFilter): number {
    const priority = IssuePriorities.find(
        ({ label }) => label.toLowerCase() === priorityFilter
    );
    return priority?.priorityValue ?? 3; // Default to medium priority if not found
}

export type LocationIssuesWidgetRef = {
    refetch: () => Promise<unknown>;
};

type LocationIssuesWidgetProps = Readonly<{
    location: ILocationResponse;
}>;

function LocationIssuesWidget(
    { location }: LocationIssuesWidgetProps,
    ref: React.ForwardedRef<LocationIssuesWidgetRef>
) {
    const [filters, setFilters] = useState<LocationIssuesFilters>(defaultFilters);
    const [isRefetching, setIsRefetching] = useState(false);

    const {
        results,
        sort,
        setSort,
        setFilter,
        isLoading,
        hasNext,
        loadNext,
        refetch,
        updateData,
    } = useInfiniteMachineHealthIssuesDataSource({
        name: `location-${location.Id}-issues`,
        defaultFilter: {
            locationId: location.Id,
            statusType: filters.statusFilters.length
                ? filters.statusFilters.map(getStatusType)
                : ACTIVE_ISSUE_STATUSES,
            severity: filters.severityFilters,
            priority: filters.priorityFilters.map(getPriorityValue),
            suffixExpression: buildFilterExpression(
                'machineHealth',
                { created: TWO_WEEKS_AGO, lastActivity: TWO_WEEKS_AGO },
                'or'
            ),
        },
        defaultSort: IssuesDefaultSort,
        keepPreviousData: true,
        refetchInterval: REFRESH_INTERVAL,
        staleTime: REFRESH_INTERVAL,
    });

    useOnIssueEvent((event) => {
        if (event.type === 'issue' && event.action === 'update') {
            refetch();
        }

        if (event.type === 'flag') {
            const issue = results?.find((issue) => issue.Id === event.issueId);

            if (issue) {
                const updatedIssue = {
                    ...issue,
                    IsFlagged: event.flagInfo.flagged,
                    ShouldReceiveNotifications: event.flagInfo.shouldReceiveNotifications,
                };
                updateData([updatedIssue], getId);
            }
        }
    });

    useImperativeHandle(ref, () => ({
        refetch: async () => {
            setIsRefetching(true);
            try {
                await refetch();
            } finally {
                setIsRefetching(false);
            }
        },
    }));

    const handleFiltersChange = useCallback(
        (newFilters: LocationIssuesFilters) => {
            setFilters(newFilters);
            setFilter((current) => ({
                ...current,
                locationId: location.Id,
                statusType: newFilters.statusFilters.length
                    ? newFilters.statusFilters.map(getStatusType)
                    : ACTIVE_ISSUE_STATUSES,
                severity: newFilters.severityFilters,
                priority: newFilters.priorityFilters.map(getPriorityValue),
                created: TWO_WEEKS_AGO,
            }));
        },
        [setFilter, location.Id]
    );

    const handleSort = useCallback(
        (newSort: DataSourceSort | undefined) => {
            setSort(newSort ? [newSort] : []);
        },
        [setSort]
    );

    return (
        <WidgetContainer>
            <LocationIssuesHeader
                location={location}
                filters={filters}
                onFiltersChange={handleFiltersChange}
            />
            <LocationIssuesTable
                results={results}
                sort={sort}
                onSort={handleSort}
                isLoading={isLoading || isRefetching}
                hasNext={hasNext}
                onLoadMore={loadNext}
            />
        </WidgetContainer>
    );
}

const memoLocationIssuesWidget = memo(
    forwardRef<LocationIssuesWidgetRef, LocationIssuesWidgetProps>(LocationIssuesWidget)
);
export { memoLocationIssuesWidget as LocationIssuesWidget };
